import React from 'react';
import { compose } from 'redux';

import { PapiDeviation } from '@wix/da-types-papi';
import withSectionFetchSpinner from '../../../../decorators/withSectionFetchSpinner';
import LoadingIndicator from '@wix/da-shared-react/pkg/LoadingIndicator';
import Button from '@wix/da-shared-react/pkg/Button';
import OnScrollBottom, {
  DISTANCE_UNIT,
} from '@wix/da-shared-react/pkg/OnScrollBottom';
import WidgetHeader from '../../../UserWidgets/_partials/WidgetHeader';
import EmptyState from '../_partials/EmptyState';
import PostsGrid from '../_partials/PostsGrid';
import PostsMobileList from '../_partials/PostsMobileList';
import SortOrderSwitcher from '../_partials/SortOrderSwitcher';
import { PostsSortOrder } from '../../../../../types/posts';
import { USER_PROFILE_POSTS_SUB_PATHS } from '@wix/da-url';
import { withTranslation, WithTranslation } from 'react-i18next';
import { ModalType } from '@wix/da-shared-react/pkg/Modals/redux/types';
import {
  withLogBiEvent,
  WithLogBiEventProps,
} from '@wix/da-shared-react/pkg/biLogger/hocs/withLogBiEvent';
import { BiEventType } from '@wix/da-shared-react/pkg/biLogger';
import ErrorBoundary from '@wix/da-shared-react/pkg/ErrorBoundary';
import { withMobileContext } from '@wix/da-shared-react/pkg/Context/MobileContext';

import s from './Polls.scss';

export interface Props {
  streamId: string;
  items: PapiDeviation[];
  hasMore: boolean;
  isFetching: boolean;
  fetchMore: () => void;
  sortOrder: PostsSortOrder;
  onSortOrderChange: (
    path: USER_PROFILE_POSTS_SUB_PATHS,
    sortOrder: PostsSortOrder
  ) => void;
  openCreatePollModal: () => void;
  isOwner: boolean;
  children?: React.ReactNode;
}

const enhance = compose(
  withSectionFetchSpinner,
  withMobileContext,
  withTranslation()
);
export class Polls extends React.Component<
  Props & WithTranslation & WithLogBiEventProps & { isMobile?: boolean }
> {
  render() {
    const {
      streamId,
      items,
      hasMore,
      isFetching,
      fetchMore,
      sortOrder,
      isOwner,
      isMobile,
      t,
    } = this.props;

    if (!items.length && !isFetching) {
      return (
        <EmptyState
          className={s['empty-state']}
          view="large"
          postType="poll"
          CTAButtonModalType={ModalType.POLL_EDIT}
        />
      );
    }

    return (
      <div>
        <ErrorBoundary>
          {isMobile ? (
            <>
              <Button
                view="b16-undisciplined-designers"
                withPaddings
                className={s['add-new']}
                onClick={this.onClickCreatePoll}
              >
                {t!('common.addNew')}
              </Button>
              <PostsMobileList streamId={streamId} items={items} />
            </>
          ) : (
            <>
              <WidgetHeader
                extras={
                  <>
                    <SortOrderSwitcher
                      className={s['sort-order-switch']}
                      value={sortOrder}
                      onChange={this.updateSortOrder}
                    />
                    {isOwner && (
                      <Button
                        view="b16-undisciplined-designers"
                        withPaddings
                        className={s['add-new']}
                        onClick={this.onClickCreatePoll}
                      >
                        {t!('common.addNew')}
                      </Button>
                    )}
                  </>
                }
              />
              <PostsGrid streamId={streamId} items={items} />
            </>
          )}
          {isFetching && <LoadingIndicator />}
          <OnScrollBottom
            distanceToBottom={2}
            distanceUnit={DISTANCE_UNIT.WINDOW_HEIGHT}
            onScrolledToBottom={hasMore && !isFetching ? fetchMore : undefined}
          />
        </ErrorBoundary>
      </div>
    );
  }

  onClickCreatePoll = event => {
    const { openCreatePollModal, logBiEvent } = this.props;
    openCreatePollModal && openCreatePollModal();
    logBiEvent({
      evid: BiEventType.ADD_NEW_CONTENT_CLICK,
      widgetname: 'polls_headerbutton',
    });
  };

  updateSortOrder = sortOrder => {
    const { onSortOrderChange } = this.props;
    onSortOrderChange &&
      onSortOrderChange(USER_PROFILE_POSTS_SUB_PATHS.POLLS, sortOrder);
  };
}

export default enhance(withLogBiEvent(Polls)) as React.ComponentType<Props>;
